import React, {
  useContext,
  useState,
  useEffect,
  createContext,
  ReactNode,
  useRef,
} from "react";
import { auth } from "./firebase-config";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  UserCredential,
  setPersistence,
  browserLocalPersistence,
  multiFactor,
} from "firebase/auth";
import { getOrganizationByUserId } from "../services/userServices/UserOrganizationService";
import { getRoleByUserId } from "../services/userServices/UserRolesService";
import Role from "../interfaces/identity/Role";
import Organization from "../interfaces/identity/Organization";
import {
  getUserInfoById,
  loginWithBackend,
} from "../services/userServices/UserInfoService";
import UserInfo from "../interfaces/UserInfo";
import { fetchAndStoreAvatars } from "../services/fileServices/FileService";
import { getPermissions } from "../services/permissionServices/PermissionService";
import {
  AbacPolicy,
  ResourceContext,
  doesUserHavePermission,
} from "../utilities/permissionUtils/PermissionUtils";
import { confirmPasswordChangeComplete } from "../services/userServices/UserInfoService";
// 1. Import the new hook
import { useSessionSync } from "../hooks/useSessionSync"; 

// Auth context interface
interface AuthContextType {
  currentUser: User | null;
  currentRoles: Role[] | null;
  currentOrganization: Organization | null;
  currentUserInfo: UserInfo | null;
  currentPolicies: AbacPolicy[];
  hasPermission: (action: string) => boolean;
  login: (email: string, password: string) => Promise<UserCredential>;
  register: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  isLoading: boolean;
  needsEmailVerification: boolean;
  needsMfaEnrollment: boolean;
  requiresPasswordChange: boolean | null;
  finalizePasswordChange: () => Promise<boolean>;
}

// Creating the context
const AuthContext = createContext<AuthContextType>({} as AuthContextType);
export default AuthContext;

// Interface for the AuthProvider props
interface AuthProviderProps {
  children: ReactNode;
}

// Provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentRoles, setCurrentRoles] = useState<Role[] | null>(null);
  const [currentOrganization, setCurrentOrganization] =
    useState<Organization | null>(null);
  const [currentUserInfo, setCurrentUserInfo] = useState<UserInfo | null>(null);
  const [currentPolicies, setCurrentPolicies] = useState<AbacPolicy[]>([]);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [needsMfaEnrollment, setNeedsMfaEnrollment] = useState(false);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState<
    boolean | null
  >(null);

  // ------------------------------------------------------------------
  // 2. LOGOUT FUNCTION (Defined early so it can be passed to the hook)
  // ------------------------------------------------------------------
  const logout = async () => {
    // Clear session timer if any
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    
    // Clear storage
    localStorage.clear();
    sessionStorage.clear();

    // Reset AuthProvider state
    setCurrentUser(null);
    setCurrentRoles(null);
    setCurrentOrganization(null);
    setCurrentUserInfo(null);
    setCurrentPolicies([]);
    setNeedsEmailVerification(false);
    setNeedsMfaEnrollment(false);
    setRequiresPasswordChange(null);
    setIsLoading(false);

    // 3. Broadcast logout to other tabs BEFORE signing out of Firebase
    // This tells other tabs: "User is gone, reset yourselves"
    broadcastAuthChange(null);

    // Sign out from Firebase
    await signOut(auth);
  };

  // ------------------------------------------------------------------
  // 4. INITIALIZE SESSION SYNC HOOK
  // ------------------------------------------------------------------
  // Pass the current user ID (or null) and the logout function
  const { broadcastAuthChange } = useSessionSync(currentUser?.uid || null, logout);

  useEffect(() => {
    // setting the persistence
    setPersistence(auth, browserLocalPersistence);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setIsLoading(false); 
      if (user) {
        const accessToken = await user.getIdToken();
        const backendLoginResponse = await loginWithBackend(accessToken); 

        if (
          backendLoginResponse.data &&
          typeof backendLoginResponse.data.requiresPasswordChange === "boolean"
        ) {
          setRequiresPasswordChange(
            backendLoginResponse.data.requiresPasswordChange
          );
        } else {
          setRequiresPasswordChange(false); 
        }
        setNeedsEmailVerification(!user.emailVerified);

        const userMultiFactor = multiFactor(user);
        setNeedsMfaEnrollment(userMultiFactor.enrolledFactors.length === 0);
        
        resetSessionTimer();
        
        const roles = await getRoleByUserId(accessToken); 
        const organization = await getOrganizationByUserId(accessToken); 
        const userInfo = await getUserInfoById(accessToken);
        await fetchAndStoreAvatars(accessToken);

        try {
          const abacPolicies = await getPermissions(accessToken);
          setCurrentPolicies(abacPolicies);
        } catch (err) {
          console.error("Error fetching permissions:", err);
        }

        if (roles) {
          setCurrentRoles(roles);
        }
        if (organization) {
          setCurrentOrganization(organization);
        }
        if (userInfo) {
          setCurrentUserInfo(userInfo);
        }
      }
    });
    return unsubscribe;
  }, []);

  const finalizePasswordChange = async (): Promise<boolean> => {
    if (!currentUser) {
       console.error("AuthProvider: Cannot finalize password change, no user.");
       return false;
    }

    try {
      const idToken = await currentUser.getIdToken();
      await confirmPasswordChangeComplete(idToken);

      await currentUser.getIdTokenResult(true); 
      setRequiresPasswordChange(false); 
      return true; 
    } catch (error) {
      console.error("AuthProvider: Error during password finalization:", error);
      return false; 
    }
  };

  const resetSessionTimer = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    // Set session expiry time (e.g., 12 hours)
    sessionTimeoutRef.current = setTimeout(() => {
      logout();
    }, 43200000); 
  };

  // ------------------------------------------------------------------
  // 5. UPDATE LOGIN / REGISTER TO BROADCAST
  // ------------------------------------------------------------------
  
  const login = async (email: string, password: string) => {
    // Await the result so we get the User object
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Broadcast the new User ID to other tabs
    broadcastAuthChange(userCredential.user.uid);
    
    return userCredential;
  };

  const register = async (email: string, password: string) => {
    // Await the result
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Broadcast the new User ID (Firebase auto-logs in after register)
    broadcastAuthChange(userCredential.user.uid);
    
    return userCredential;
  };

  const hasPermission = (action: string, ctx?: ResourceContext): boolean => {
    if (!currentUser) return false;
    return doesUserHavePermission(currentPolicies, action, ctx);
  };
  
  const value = {
    currentUser,
    currentRoles,
    currentOrganization,
    currentUserInfo,
    currentPolicies,
    hasPermission,
    login,
    register,
    logout,
    isLoading,
    needsEmailVerification,
    needsMfaEnrollment,
    requiresPasswordChange,
    finalizePasswordChange 
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook for using the context
export const useAuth = () => useContext(AuthContext);